"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = require("lodash");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const utils_1 = require("./utils");
// names inside this class and discovery logic respect BIP32 standard
class Xpub {
    constructor({ storage, explorer, crypto, xpub, derivationMode, }) {
        // https://github.com/bitcoinjs/bitcoinjs-lib/blob/27a840aac4a12338f1e40c54f3759bbd7a559944/src/bufferutils.js#L24
        // only works with number so we need to be sure to pass correct numbers
        this.OUTPUT_VALUE_MAX = Number.MAX_SAFE_INTEGER;
        this.GAP = 20;
        // need to be bigger than the number of tx from the same address that can be in the same block
        this.txsSyncArraySize = 1000;
        this.storage = storage;
        this.explorer = explorer;
        this.crypto = crypto;
        this.xpub = xpub;
        this.derivationMode = derivationMode;
        this.freshAddress = "";
        this.freshAddressIndex = 0;
    }
    syncAddress(account, index, needReorg) {
        return __awaiter(this, void 0, void 0, function* () {
            const address = yield this.crypto.getAddress(this.derivationMode, this.xpub, account, index);
            this.storage.addAddress(`${this.crypto.network.name}-${this.derivationMode}-${this.xpub}-${account}-${index}`, address);
            if (needReorg) {
                yield this.checkAddressReorg(account, index);
            }
            // in case pendings have changed we clean them out
            const hasPendings = this.storage.hasPendingTx({
                account,
                index,
            });
            if (hasPendings) {
                this.storage.removePendingTxs({ account, index });
            }
            yield this.fetchHydrateAndStoreNewTxs(address, account, index);
            const hasTx = this.storage.hasTx({
                account,
                index,
            });
            if (account === 0 && hasTx) {
                this.freshAddressIndex = Math.max(this.freshAddressIndex, index + 1);
            }
            return hasTx;
        });
    }
    checkAddressesBlock(account, index, needReorg) {
        return __awaiter(this, void 0, void 0, function* () {
            const addressesResults = yield Promise.all((0, lodash_1.range)(this.GAP).map((_, key) => this.syncAddress(account, index + key, needReorg)));
            return (0, lodash_1.some)(addressesResults, lastTx => !!lastTx);
        });
    }
    syncAccount(account, needReorg) {
        return __awaiter(this, void 0, void 0, function* () {
            let index = 0;
            // eslint-disable-next-line no-await-in-loop
            while (yield this.checkAddressesBlock(account, index, needReorg)) {
                index += this.GAP;
            }
            return index;
        });
    }
    // TODO : test fail case + incremental
    sync() {
        return __awaiter(this, void 0, void 0, function* () {
            this.freshAddressIndex = 0;
            const highestBlockFromStorage = this.storage.getHighestBlockHeightAndHash();
            let needReorg = !!highestBlockFromStorage;
            if (highestBlockFromStorage) {
                const highestBlockFromExplorer = yield this.explorer.getBlockByHeight(highestBlockFromStorage.height);
                if ((highestBlockFromExplorer === null || highestBlockFromExplorer === void 0 ? void 0 : highestBlockFromExplorer.hash) === highestBlockFromStorage.hash) {
                    needReorg = false;
                }
            }
            yield Promise.all([
                this.syncAccount(0, needReorg),
                this.syncAccount(1, needReorg), // for change addresses
            ]);
            this.freshAddress = yield this.crypto.getAddress(this.derivationMode, this.xpub, 0, this.freshAddressIndex);
        });
    }
    getXpubBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const addresses = yield this.getXpubAddresses();
            return this.getAddressesBalance(addresses);
        });
    }
    getAccountBalance(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const addresses = yield this.getAccountAddresses(account);
            return this.getAddressesBalance(addresses);
        });
    }
    getAddressBalance(address) {
        return __awaiter(this, void 0, void 0, function* () {
            const unspentUtxos = this.storage.getAddressUnspentUtxos(address);
            return unspentUtxos.reduce((total, { value }) => total.plus(value), new bignumber_js_1.default(0));
        });
    }
    getXpubAddresses() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.storage.getUniquesAddresses({});
        });
    }
    getAccountAddresses(account) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.storage.getUniquesAddresses({ account });
        });
    }
    getNewAddress(account, gap) {
        return __awaiter(this, void 0, void 0, function* () {
            const accountAddresses = yield this.getAccountAddresses(account);
            const lastIndex = ((0, lodash_1.maxBy)(accountAddresses, "index") || { index: -1 }).index;
            let index;
            if (lastIndex === -1) {
                index = 0;
            }
            else {
                index = lastIndex + gap;
            }
            const address = {
                address: yield this.crypto.getAddress(this.derivationMode, this.xpub, account, index),
                account,
                index,
            };
            return address;
        });
    }
    buildTx(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const outputs = [];
            const { amount, opReturnData, destAddress, changeAddress, utxoPickingStrategy, feePerByte, sequence, } = params;
            // outputs splitting
            // btc only support value fitting in uint64 and the lib
            // we use to serialize output only take js number in params
            // that are actually even more restricted
            const desiredOutputLeftToFit = {
                script: this.crypto.toOutputScript(destAddress),
                value: amount,
                address: destAddress,
                isChange: false,
            };
            while (desiredOutputLeftToFit.value.gt(this.OUTPUT_VALUE_MAX)) {
                outputs.push({
                    script: desiredOutputLeftToFit.script,
                    value: new bignumber_js_1.default(this.OUTPUT_VALUE_MAX),
                    address: destAddress,
                    isChange: false,
                });
                desiredOutputLeftToFit.value = desiredOutputLeftToFit.value.minus(this.OUTPUT_VALUE_MAX);
            }
            if (desiredOutputLeftToFit.value.gt(0)) {
                outputs.push(desiredOutputLeftToFit);
            }
            if (opReturnData) {
                const opReturnOutput = {
                    script: this.crypto.toOpReturnOutputScript(opReturnData),
                    value: new bignumber_js_1.default(0),
                    address: null,
                    isChange: false,
                };
                outputs.push(opReturnOutput);
            }
            // now we select only the output needed to cover the amount + fee
            const { totalValue: total, unspentUtxos: unspentUtxoSelected, fee, needChangeoutput, } = yield utxoPickingStrategy.selectUnspentUtxosToUse(this, outputs, feePerByte);
            const txHexs = yield Promise.all(unspentUtxoSelected.map(unspentUtxo => this.explorer.getTxHex(unspentUtxo.output_hash)));
            const txs = yield Promise.all(unspentUtxoSelected.map(unspentUtxo => this.storage.getTx(unspentUtxo.address, unspentUtxo.output_hash)));
            const inputs = unspentUtxoSelected.map((utxo, index) => {
                return {
                    txHex: txHexs[index],
                    value: utxo.value,
                    address: utxo.address,
                    output_hash: utxo.output_hash,
                    output_index: utxo.output_index,
                    sequence,
                };
            });
            const associatedDerivations = unspentUtxoSelected.map((_utxo, index) => {
                var _a, _b;
                if (txs[index] == null) {
                    throw new Error("Invalid index in txs[index]");
                }
                return [((_a = txs[index]) === null || _a === void 0 ? void 0 : _a.account) || 0, ((_b = txs[index]) === null || _b === void 0 ? void 0 : _b.index) || 0];
            });
            const txSize = (0, utils_1.maxTxSizeCeil)(unspentUtxoSelected.length, outputs.map(o => o.script), true, this.crypto, this.derivationMode);
            const dustLimit = (0, utils_1.computeDustAmount)(this.crypto, txSize);
            // Abandon the change output if change output amount is less than dust amount
            if (needChangeoutput && total.minus(amount).minus(fee).gt(dustLimit)) {
                outputs.push({
                    script: this.crypto.toOutputScript(changeAddress.address),
                    value: total.minus(amount).minus(fee),
                    address: changeAddress.address,
                    isChange: true,
                });
            }
            const outputsValue = outputs.reduce((cur, o) => cur.plus(o.value), new bignumber_js_1.default(0));
            return {
                inputs,
                associatedDerivations,
                outputs,
                fee: total.minus(outputsValue).toNumber(),
                changeAddress: changeAddress,
            };
        });
    }
    broadcastTx(rawTxHex) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.explorer.broadcast(rawTxHex);
        });
    }
    // internal
    getAddressesBalance(addresses) {
        return __awaiter(this, void 0, void 0, function* () {
            const balances = yield Promise.all(addresses.map(address => this.getAddressBalance(address)));
            return balances.reduce((total, balance) => total.plus(balance), new bignumber_js_1.default(0));
        });
    }
    fetchHydrateAndStoreNewTxs(address, account, index) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let pendingTxs = [];
            let txs = [];
            let inserted = 0;
            do {
                const lastTxBlockheight = ((_a = this.storage.getLastConfirmedTxBlock({
                    account,
                    index,
                })) === null || _a === void 0 ? void 0 : _a.height) || 0;
                if (pendingTxs.length > 0) {
                    txs = yield this.explorer.getTxsSinceBlockheight(this.txsSyncArraySize, { address, account, index }, lastTxBlockheight, false);
                    inserted += this.storage.appendTxs(txs); // insert not pending tx
                }
                else {
                    [pendingTxs, txs] = yield Promise.all([
                        this.explorer.getTxsSinceBlockheight(this.txsSyncArraySize, { address, account, index }, 0, true),
                        this.explorer.getTxsSinceBlockheight(this.txsSyncArraySize, { address, account, index }, lastTxBlockheight, false),
                    ]);
                    inserted += this.storage.appendTxs(txs); // insert not pending tx
                }
            } while (txs.length >= this.txsSyncArraySize); // check whether page is full, if not, it is the last page
            inserted += this.storage.appendTxs(pendingTxs); // insert pending tx
            return inserted;
        });
    }
    checkAddressReorg(account, index) {
        return __awaiter(this, void 0, void 0, function* () {
            const lastConfirmedTxBlock = this.storage.getLastConfirmedTxBlock({
                account,
                index,
            });
            if (!lastConfirmedTxBlock) {
                return;
            }
            const block = yield this.explorer.getBlockByHeight(lastConfirmedTxBlock.height);
            // all good the block is valid
            if (block && block.hash === lastConfirmedTxBlock.hash) {
                return;
            }
            // in this case the block is not valid so we delete everything
            // for that address
            // TODO: delete only everything for this (address, block)
            // but need to think if its possible with current storage implem
            this.storage.removeTxs({
                account,
                index,
            });
        });
    }
}
exports.default = Xpub;
//# sourceMappingURL=xpub.js.map